import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  DEFAULT_OG_IMAGE,
  SITE_NAME,
  getCanonicalUrl,
  resolveRouteSeo,
} from "../config/seo";

const upsertMeta = (attribute, key, content) => {
  if (!content) return null;
  let el = document.querySelector(`meta[${attribute}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attribute, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return el;
};

const upsertLink = (rel, href) => {
  if (!href) return null;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
};

const upsertJsonLd = (id, data) => {
  const existing = document.getElementById(id);
  if (!data) {
    existing?.remove();
    return null;
  }

  let el = existing;
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
  return el;
};

export const usePageSeo = (override = undefined) => {
  const { pathname, search } = useLocation();

  useEffect(() => {
    if (override === null) return undefined;

    const routeSeo =
      override !== undefined ? override : resolveRouteSeo(pathname);
    if (routeSeo === null) return undefined;

    const previousTitle = document.title;
    const title = routeSeo.title || SITE_NAME;
    const description = routeSeo.description || SITE_NAME;
    const canonical = routeSeo.canonical || getCanonicalUrl(pathname);

    document.title = title;

    const descriptionMeta = document.querySelector('meta[name="description"]');
    const previousDescription = descriptionMeta?.getAttribute("content") || "";
    const metaDescription = upsertMeta("name", "description", description);
    const keywordsMeta = upsertMeta("name", "keywords", routeSeo.keywords);

    const robotsContent = routeSeo.noindex
      ? "noindex, nofollow"
      : "index, follow";
    const robotsMeta = upsertMeta("name", "robots", robotsContent);

    const canonicalLink = upsertLink("canonical", canonical);

    const ogTitle = upsertMeta("property", "og:title", title);
    const ogDescription = upsertMeta("property", "og:description", description);
    const ogUrl = upsertMeta("property", "og:url", canonical);
    const ogType = upsertMeta(
      "property",
      "og:type",
      routeSeo.ogType || "website",
    );
    const ogSiteName = upsertMeta("property", "og:site_name", SITE_NAME);
    const ogImage = upsertMeta(
      "property",
      "og:image",
      routeSeo.ogImage || DEFAULT_OG_IMAGE,
    );

    const twitterCard = upsertMeta(
      "name",
      "twitter:card",
      "summary_large_image",
    );
    const twitterTitle = upsertMeta("name", "twitter:title", title);
    const twitterDescription = upsertMeta(
      "name",
      "twitter:description",
      description,
    );
    const twitterImage = upsertMeta(
      "name",
      "twitter:image",
      routeSeo.ogImage || DEFAULT_OG_IMAGE,
    );

    const jsonLd = upsertJsonLd("azzir-json-ld", routeSeo.jsonLd || null);

    return () => {
      document.title = previousTitle;
      if (metaDescription) {
        metaDescription.setAttribute("content", previousDescription);
      }
      keywordsMeta?.remove();
      robotsMeta?.remove();
      canonicalLink?.remove();
      ogTitle?.remove();
      ogDescription?.remove();
      ogUrl?.remove();
      ogType?.remove();
      ogSiteName?.remove();
      ogImage?.remove();
      twitterCard?.remove();
      twitterTitle?.remove();
      twitterDescription?.remove();
      twitterImage?.remove();
      jsonLd?.remove();
    };
  }, [pathname, search, override]);
};

export default usePageSeo;
